<template>
    <section class="recipe-section ptb-100">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <div class="section-heading text-center mb-5">
                        <h2>{{ $t("recipesHeading") }}</h2>
                        <p>{{ $t("recipesSub") }}</p>
                    </div>
                </div>
            </div>

            <!-- Which language the helper reads. Everything on the page is
                 shown in Chinese alongside this, never instead of it. -->
            <div class="rc-langbar">
                <span class="rc-langbar__label">{{ $t("recipesHelperLang") }}</span>
                <div class="rc-toggle">
                    <button
                        v-for="l in HELPER_LANGS"
                        :key="l.key"
                        type="button"
                        class="rc-toggle__btn"
                        :class="{ 'is-active': helperLang === l.key }"
                        @click="setHelperLang(l.key)"
                    >
                        {{ l.label }}
                    </button>
                </div>
            </div>

            <div v-if="loading" class="text-center py-5">
                <div class="rc-spinner"></div>
                <p class="mt-3 mb-0">{{ $t("recipesLoading") }}</p>
            </div>

            <div v-else-if="error" class="text-center py-5">
                <p class="mb-3">{{ $t("recipesError") }}</p>
                <button class="rc-btn rc-btn--primary" @click="load">
                    {{ $t("recipesRetry") }}
                </button>
            </div>

            <template v-else>
                <!-- Filters -->
                <div class="rc-filters">
                    <input
                        v-model="q"
                        type="search"
                        class="rc-search"
                        :placeholder="$t('recipesSearch')"
                    />
                    <!-- Page-level portion picker, mirroring the source's
                         serving tabs. Shares state with the one in the modal. -->
                    <div class="rc-servings rc-servings--top" v-if="servings.length">
                        <span class="rc-servings__label">{{ $t("recipesServing") }}</span>
                        <div class="rc-toggle">
                            <button
                                v-for="s in servings"
                                :key="s.key"
                                type="button"
                                class="rc-toggle__btn"
                                :class="{ 'is-active': serving === s.key }"
                                @click="serving = s.key"
                            >
                                {{ s.zh }}
                            </button>
                        </div>
                    </div>

                    <div class="rc-chips">
                        <button
                            v-for="c in categories"
                            :key="c.key"
                            type="button"
                            class="rc-chip"
                            :class="{ 'is-active': cat === c.key }"
                            @click="cat = c.key"
                        >
                            <span class="rc-chip__emoji">{{ c.emoji }}</span>
                            {{ c.zh }}
                            <span class="rc-chip__count">{{ countFor(c.key) }}</span>
                        </button>
                    </div>
                    <div class="rc-subfilters">
                        <button
                            v-for="d in DIFFS"
                            :key="d.key"
                            type="button"
                            class="rc-pill"
                            :class="{ 'is-active': diff === d.key }"
                            @click="diff = d.key"
                        >
                            {{ $t(d.i18n) }}
                        </button>
                        <button
                            type="button"
                            class="rc-pill"
                            :class="{ 'is-active': sortByTime }"
                            @click="sortByTime = !sortByTime"
                        >
                            ⏱ {{ $t("recipesQuickest") }}
                        </button>
                        <button
                            type="button"
                            class="rc-pill rc-pill--surprise"
                            :disabled="!filtered.length"
                            @click="surprise"
                        >
                            🎲 {{ $t("recipesSurprise") }}
                        </button>
                    </div>
                </div>

                <p class="rc-count">{{ $t("recipesCount", { n: filtered.length }) }}</p>

                <div v-if="!filtered.length" class="text-center py-5">
                    <p class="mb-0">{{ $t("recipesEmpty") }}</p>
                </div>

                <!-- Grid -->
                <div class="rc-grid">
                    <button
                        v-for="r in filtered"
                        :key="r.zh"
                        type="button"
                        class="rc-card"
                        @click="open(r)"
                    >
                        <div class="rc-card__emoji">{{ r.emoji }}</div>
                        <div class="rc-card__body">
                            <h5 class="rc-card__zh">{{ r.zh }}</h5>
                            <p class="rc-card__alt">{{ r[helperLang] }}</p>
                            <div class="rc-card__meta">
                                <span v-if="activeServing" class="rc-tag rc-tag--serving">
                                    👥 {{ activeServing.zhBadge }}
                                </span>
                                <span class="rc-tag">⏱ {{ r.timeMin }}{{ $t("recipesMin") }}</span>
                                <span
                                    class="rc-tag"
                                    :class="r.diff === 'easy' ? 'rc-tag--easy' : 'rc-tag--med'"
                                >{{ $t(r.diff === "easy" ? "recipesEasy" : "recipesMedium") }}</span>
                            </div>
                        </div>
                    </button>
                </div>
            </template>
        </div>

        <!-- Detail modal. Hand-rolled rather than v-dialog: the app has no
             <v-app> root, which Vuetify overlays require. -->
        <div ref="modalRoot" v-show="selected" class="rc-modal" @click.self="close">
            <div v-if="selected" class="rc-modal__panel" role="dialog" aria-modal="true">
                <header class="rc-modal__head">
                    <div>
                        <h3 class="rc-modal__zh">
                            <span class="rc-modal__emoji">{{ selected.emoji }}</span>
                            {{ selected.zh }}
                        </h3>
                        <p class="rc-modal__alt">{{ selected[helperLang] }}</p>
                    </div>
                    <button class="rc-close" type="button" :aria-label="$t('recipesClose')" @click="close">×</button>
                </header>

                <div class="rc-modal__meta">
                    <!-- Matches the source's servings badge: how many people the
                         quantities below are currently written for. -->
                    <span v-if="activeServing" class="rc-tag rc-tag--serving">
                        👥 {{ activeServing.zhBadge }} ·
                        {{ helperLang === "id" ? activeServing.id : activeServing.fil }}
                    </span>
                    <span class="rc-tag">⏱ {{ selected.timeMin }}{{ $t("recipesMin") }}</span>
                    <span
                        class="rc-tag"
                        :class="selected.diff === 'easy' ? 'rc-tag--easy' : 'rc-tag--med'"
                    >{{ $t(selected.diff === "easy" ? "recipesEasy" : "recipesMedium") }}</span>
                    <button
                        v-if="SHOW_PRINT"
                        class="rc-btn rc-btn--ghost rc-print"
                        type="button"
                        @click="print"
                    >
                        🖨 {{ $t("recipesPrint") }}
                    </button>
                </div>

                <div class="rc-modal__body">
                    <div class="rc-servings" v-if="servings.length">
                        <span class="rc-servings__label">{{ $t("recipesServing") }}</span>
                        <div class="rc-toggle">
                            <button
                                v-for="s in servings"
                                :key="s.key"
                                type="button"
                                class="rc-toggle__btn"
                                :class="{ 'is-active': serving === s.key }"
                                @click="serving = s.key"
                            >
                                {{ s.zh }}
                                <small class="rc-toggle__alt">{{ helperLang === "id" ? s.id : s.fil }}</small>
                            </button>
                        </div>
                    </div>

                    <h4 class="rc-sub">{{ $t("recipesIngredients") }}</h4>
                    <table class="rc-table">
                        <tbody>
                            <tr v-for="(row, i) in selected.ing" :key="'i' + i">
                                <td class="rc-td-zh">
                                    {{ row[ING.ZH] }}
                                    <span class="rc-qty">{{ scaled(row[ING.QTY_ZH], "zh") }}</span>
                                </td>
                                <td class="rc-td-alt">
                                    {{ row[helperLang === "id" ? ING.ID : ING.FIL] }}
                                    <span class="rc-qty">{{
                                        scaled(
                                            row[helperLang === "id" ? ING.QTY_ID : ING.QTY_FIL],
                                            helperLang
                                        )
                                    }}</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <h4 class="rc-sub mt-4">{{ $t("recipesSteps") }}</h4>
                    <!-- Quantities above are scaled; the instructions below are
                         the author's original prose. Say so rather than let the
                         two silently disagree. -->
                    <p v-if="ratio !== 1" class="rc-note">
                        ⓘ {{ $t("recipesStepsNote") }}
                    </p>
                    <ol class="rc-steps">
                        <li v-for="(s, i) in selected.steps" :key="'s' + i">
                            <p class="rc-step-zh">{{ s[STEP.ZH] }}</p>
                            <p class="rc-step-alt">{{ s[helperLang === "id" ? STEP.ID : STEP.FIL] }}</p>
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import axios from "axios";
import { scaleQty, ratioFor } from "../../utils/scaleQty";

// Column positions inside the compact source arrays. Named so the template
// reads as intent rather than magic indices.
const ING = { ZH: 0, ID: 1, FIL: 2, QTY_ZH: 3, QTY_ID: 4, QTY_FIL: 5 };
const STEP = { ZH: 0, ID: 1, FIL: 2 };

const HELPER_LANGS = [
    { key: "id", label: "Bahasa Indonesia" },
    { key: "fil", label: "Filipino" },
];
const DIFFS = [
    { key: "all", i18n: "recipesAll" },
    { key: "easy", i18n: "recipesEasy" },
    { key: "medium", i18n: "recipesMedium" },
];
const LANG_KEY = "hhs.helperLang";

// Print is hidden pending an unresolved rendering issue: Chinese glyphs drop
// out of the printed output while Latin text and digits survive, so a printed
// recipe reaches the helper with the ingredient names missing. The print
// stylesheet and print() are left in place — flip this back to true once the
// font selection is sorted out.
const SHOW_PRINT = false;

export default {
    name: "RecipeBrowser",
    data() {
        return {
            ING,
            STEP,
            HELPER_LANGS,
            DIFFS,
            SHOW_PRINT,
            loading: true,
            error: false,
            recipes: [],
            categories: [],
            servings: [],
            serving: "s2", // the bucket the recipes are written closest to
            cat: "all",
            diff: "all",
            q: "",
            sortByTime: false,
            helperLang: "id",
            selected: null,
            lastPick: null,
        };
    },
    computed: {
        filtered() {
            const q = this.q.trim().toLowerCase();
            let list = this.recipes.filter((r) => {
                if (this.cat !== "all" && r.cat !== this.cat) return false;
                if (this.diff !== "all" && r.diff !== this.diff) return false;
                if (!q) return true;
                // Search names in all three languages plus ingredient names, so
                // "chicken", "ayam", "manok" and 雞 all find the same dishes.
                if (`${r.zh} ${r.id} ${r.fil}`.toLowerCase().includes(q)) return true;
                return r.ing.some((row) =>
                    `${row[ING.ZH]} ${row[ING.ID]} ${row[ING.FIL]}`.toLowerCase().includes(q)
                );
            });
            if (this.sortByTime) list = list.slice().sort((a, b) => a.timeMin - b.timeMin);
            return list;
        },
        activeServing() {
            return this.servings.find((s) => s.key === this.serving) || null;
        },
        ratio() {
            return ratioFor(this.activeServing);
        },
    },
    created() {
        const saved = this.readSavedLang();
        if (saved) this.helperLang = saved;
        this.load();
    },
    mounted() {
        document.addEventListener("keydown", this.onKey);
        // Reparent the modal to <body> so printing can hide everything else with
        // display:none. Buried inside #app there is no selector that hides its
        // ancestors without hiding it too, and visibility:hidden leaves the page
        // occupying layout — which is what produced the blank pages.
        // v-show (not v-if) on this node keeps Vue from ever removing it from a
        // parent it no longer lives in.
        if (this.$refs.modalRoot) document.body.appendChild(this.$refs.modalRoot);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.onKey);
        document.body.classList.remove("rc-noscroll");
        const el = this.$refs.modalRoot;
        if (el && el.parentNode === document.body) document.body.removeChild(el);
    },
    methods: {
        async load() {
            this.loading = true;
            this.error = false;
            try {
                // Served from public/, so it stays out of the JS bundle and is
                // cached by the browser like any other static asset.
                const { data } = await axios.get(`${process.env.BASE_URL}data/recipes.json`);
                this.recipes = data.recipes || [];
                this.categories = data.categories || [];
                this.servings = data.servings || [];
            } catch (e) {
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        countFor(key) {
            if (key === "all") return this.recipes.length;
            return this.recipes.filter((r) => r.cat === key).length;
        },
        readSavedLang() {
            try {
                const v = window.localStorage.getItem(LANG_KEY);
                return v === "id" || v === "fil" ? v : null;
            } catch (e) {
                return null; // private browsing / storage disabled
            }
        },
        setHelperLang(key) {
            this.helperLang = key;
            try {
                window.localStorage.setItem(LANG_KEY, key);
            } catch (e) {
                /* non-critical */
            }
        },
        open(r) {
            this.selected = r;
            document.body.classList.add("rc-noscroll");
        },
        close() {
            this.selected = null;
            document.body.classList.remove("rc-noscroll");
        },
        onKey(e) {
            if (e.key === "Escape" && this.selected) this.close();
        },
        /**
         * Opens a random dish from whatever is currently filtered, so a chosen
         * category or search still applies. Re-rolls once if it lands on the
         * dish just shown — with a category narrowed to three items, repeats
         * otherwise feel broken rather than random.
         */
        surprise() {
            const list = this.filtered;
            if (!list.length) return;
            let pick = list[Math.floor(Math.random() * list.length)];
            if (list.length > 1 && pick.zh === this.lastPick) {
                const others = list.filter((r) => r.zh !== this.lastPick);
                pick = others[Math.floor(Math.random() * others.length)];
            }
            this.lastPick = pick.zh;
            this.open(pick);
        },
        /** Ingredient quantities only — step text is never rewritten. */
        scaled(qty, lang) {
            return scaleQty(qty, this.ratio, lang);
        },
        print() {
            window.print();
        },
    },
};
</script>

<style scoped>
.recipe-section {
    background: #fff;
}

/* ── language bar ─────────────────────────────────────────────────────────── */
.rc-langbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 28px;
}
.rc-langbar__label {
    font-size: 14px;
    color: #6b7b7b;
    font-weight: 600;
}
.rc-toggle {
    display: inline-flex;
    border: 1px solid #d8e6e6;
    border-radius: 999px;
    overflow: hidden;
}
.rc-toggle__btn {
    border: 0;
    background: #fff;
    padding: 8px 18px;
    font-size: 14px;
    font-weight: 600;
    color: #4a5a5a;
    cursor: pointer;
}
.rc-toggle__btn.is-active {
    background: #4cb7b7;
    color: #fff;
}

/* ── filters ──────────────────────────────────────────────────────────────── */
.rc-filters {
    margin-bottom: 20px;
}
.rc-search {
    width: 100%;
    max-width: 460px;
    display: block;
    margin: 0 auto 18px;
    padding: 12px 18px;
    border: 1.5px solid #dfe9e9;
    border-radius: 999px;
    font-size: 15px;
    outline: none;
}
.rc-search:focus {
    border-color: #4cb7b7;
}
.rc-chips,
.rc-subfilters {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
}
.rc-subfilters {
    margin-top: 12px;
}
.rc-chip {
    border: 1.5px solid #e2eded;
    background: #fff;
    border-radius: 999px;
    padding: 7px 14px;
    font-size: 14px;
    color: #38484a;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.rc-chip.is-active {
    background: #4cb7b7;
    border-color: #4cb7b7;
    color: #fff;
}
.rc-chip__emoji {
    font-size: 15px;
}
.rc-chip__count {
    font-size: 11px;
    opacity: 0.65;
}
.rc-pill {
    border: 1.5px solid #e2eded;
    background: #fff;
    border-radius: 999px;
    padding: 5px 14px;
    font-size: 13px;
    color: #4a5a5a;
    cursor: pointer;
}
.rc-pill--surprise {
    border-color: #fe7e6d;
    color: #d8503c;
    font-weight: 700;
}
.rc-pill--surprise:hover:not(:disabled) {
    background: #fe7e6d;
    border-color: #fe7e6d;
    color: #fff;
}
.rc-pill--surprise:disabled {
    opacity: 0.45;
    cursor: default;
}
.rc-pill.is-active {
    background: #fe7e6d;
    border-color: #fe7e6d;
    color: #fff;
}
.rc-count {
    text-align: center;
    color: #7a8a8a;
    font-size: 13px;
    margin: 18px 0 22px;
}

/* ── grid ─────────────────────────────────────────────────────────────────── */
.rc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 16px;
}
.rc-card {
    display: flex;
    align-items: center;
    gap: 14px;
    text-align: left;
    background: #fff;
    border: 1px solid #e8f0f0;
    border-radius: 14px;
    padding: 14px;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.rc-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(10, 60, 60, 0.1);
    border-color: #cfe6e6;
}
.rc-card__emoji {
    font-size: 30px;
    width: 52px;
    height: 52px;
    flex: 0 0 52px;
    border-radius: 12px;
    background: #eef8f8;
    display: flex;
    align-items: center;
    justify-content: center;
}
.rc-card__body {
    min-width: 0;
}
.rc-card__zh {
    margin: 0 0 2px;
    font-size: 16px;
    font-weight: 700;
    color: #1d2c2c;
}
.rc-card__alt {
    margin: 0 0 6px;
    font-size: 12px;
    color: #7d8d8d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.rc-card__meta {
    flex-wrap: wrap;
    display: flex;
    gap: 6px;
}
.rc-tag {
    font-size: 11px;
    background: #f1f6f6;
    color: #5d6f6f;
    border-radius: 999px;
    padding: 2px 9px;
}
.rc-tag--serving {
    background: #e4f3f3;
    color: #22706e;
    font-weight: 700;
}
.rc-tag--easy {
    background: #e6f6ec;
    color: #2e7d4f;
}
.rc-tag--med {
    background: #fdf0e6;
    color: #b97324;
}

/* ── modal ────────────────────────────────────────────────────────────────── */
.rc-modal {
    position: fixed;
    inset: 0;
    background: rgba(12, 30, 30, 0.55);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 24px 16px;
    overflow-y: auto;
    z-index: 1200;
}
.rc-modal__panel {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 760px;
    padding: 24px;
    margin: auto;
}
.rc-modal__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
}
.rc-modal__zh {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    color: #16211f;
}
.rc-modal__emoji {
    margin-right: 6px;
}
.rc-modal__alt {
    margin: 4px 0 0;
    color: #7d8d8d;
    font-size: 14px;
}
.rc-close {
    border: 0;
    background: #f1f6f6;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    color: #4a5a5a;
    flex: 0 0 34px;
}
.rc-modal__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 14px 0 20px;
    flex-wrap: wrap;
}
.rc-print {
    margin-left: auto;
}
.rc-servings--top {
    justify-content: center;
    border-bottom: 0;
    padding-bottom: 0;
    margin-bottom: 18px;
}
.rc-servings {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 22px;
    padding-bottom: 18px;
    border-bottom: 1px solid #eef4f4;
}
.rc-servings__label {
    font-size: 13px;
    font-weight: 700;
    color: #4a5a5a;
}
.rc-toggle__alt {
    display: block;
    font-size: 10px;
    font-weight: 500;
    opacity: 0.75;
    line-height: 1.2;
}
.rc-note {
    background: #fdf6ec;
    border-left: 3px solid #d99a55;
    color: #8a6430;
    font-size: 12.5px;
    line-height: 1.55;
    padding: 9px 12px;
    border-radius: 6px;
    margin: 0 0 14px;
}
.rc-sub {
    font-size: 15px;
    font-weight: 700;
    color: #4cb7b7;
    margin: 0 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.rc-table {
    width: 100%;
    border-collapse: collapse;
}
.rc-table td {
    padding: 9px 10px;
    border-bottom: 1px solid #eef4f4;
    vertical-align: top;
    font-size: 14px;
}
.rc-td-zh {
    width: 50%;
    color: #1d2c2c;
    font-weight: 600;
}
.rc-td-alt {
    width: 50%;
    color: #5f7070;
}
.rc-qty {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #8a9a9a;
    margin-top: 1px;
}
.rc-steps {
    padding-left: 20px;
    margin: 0;
}
.rc-steps li {
    margin-bottom: 14px;
}
.rc-step-zh {
    margin: 0;
    font-size: 14px;
    color: #1d2c2c;
    line-height: 1.6;
}
.rc-step-alt {
    margin: 3px 0 0;
    font-size: 13px;
    color: #6f8080;
    line-height: 1.6;
}

/* ── buttons / misc ───────────────────────────────────────────────────────── */
.rc-btn {
    border: 0;
    border-radius: 999px;
    padding: 7px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.rc-btn--primary {
    background: #4cb7b7;
    color: #fff;
}
.rc-btn--ghost {
    background: #f1f6f6;
    color: #4a5a5a;
}
.rc-spinner {
    width: 34px;
    height: 34px;
    border: 3px solid #e4f0f0;
    border-top-color: #4cb7b7;
    border-radius: 50%;
    margin: 0 auto;
    animation: rc-spin 0.8s linear infinite;
}
@keyframes rc-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 575px) {
    .rc-modal__panel {
        padding: 18px;
    }
    .rc-table td {
        font-size: 13px;
    }
}
</style>

<style>
/* Unscoped: the modal locks the page behind it, and printing a recipe should
   yield just the recipe — employers print these for the helper to keep. */
body.rc-noscroll {
    overflow: hidden;
}

@media print {
    /* The modal is a direct child of <body> (see mounted()), so every other
       branch can be removed outright. display:none rather than
       visibility:hidden — hidden elements still take up space and paginate
       into blank sheets. */
    body.rc-noscroll > *:not(.rc-modal) {
        display: none !important;
    }
    body.rc-noscroll {
        overflow: visible !important;
    }
    body.rc-noscroll .rc-modal {
        position: static !important;
        background: none !important;
        padding: 0 !important;
        overflow: visible !important;
        display: block !important;
    }
    body.rc-noscroll .rc-modal__panel {
        max-width: none !important;
        padding: 0 !important;
        border-radius: 0 !important;
        box-shadow: none !important;
    }
    /* The site's Open Sans / Montserrat carry no CJK or emoji glyphs. On screen
       the browser quietly falls back to a system font; print engines frequently
       do not, and Chinese comes out blank or as tofu. Name the fallbacks so
       there is nothing to guess. Headings are included because main.css sets
       Montserrat on h1-h6 directly. */
    body.rc-noscroll .rc-modal,
    body.rc-noscroll .rc-modal * {
        font-family: "Open Sans", "PingFang HK", "PingFang TC", "Hiragino Sans CNS",
            "Microsoft JhengHei", "Noto Sans TC", "Noto Sans CJK TC", "Heiti TC",
            "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif !important;
    }
    /* Chrome drops backgrounds by default, which can take low-contrast text
       with it. Force the recipe text to solid black on paper. */
    body.rc-noscroll .rc-modal,
    body.rc-noscroll .rc-modal * {
        color: #000 !important;
    }
    /* Controls are meaningless on paper. The portion badge in the header
       already states who the quantities are for. */
    body.rc-noscroll .rc-close,
    body.rc-noscroll .rc-print,
    body.rc-noscroll .rc-servings {
        display: none !important;
    }
    /* Don't split an ingredient row or a step across a page break. */
    body.rc-noscroll .rc-table tr,
    body.rc-noscroll .rc-steps li {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    @page {
        margin: 14mm;
    }
}
</style>
